import { provideNetlifyLoader } from '@angular/common';
import { provideHttpClient, withFetch } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
  withI18nSupport,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { provideRouter, Router, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import * as Sentry from '@sentry/angular';
import {
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js';
import { provideCharts } from 'ng2-charts';
import { routes } from './app.routes';
import { portalDomainProviders } from './portal/domain/domain-providers';
import { REPORT_JOB_POLLER } from './portal/domain/report-job/report-job-poller';
import { pollJob } from './portal/shared/utils/job-polling.util';
import { AltuidCookieService } from './shared/services/altuid-cookie.service';
import { FilteredSentryErrorHandler } from './shared/services/sentry-error-handler';
import { VendorRegistryService } from './shared/services/vendor-registry.service';
import './portal/shared/utils/chart-tooltip-positioner';
import { environment } from 'src/environments/environment';
import { COUNTRY_CONFIG } from './shared/country/country.config';

// Eagerly register Chart.js components at module load. ng2-charts'
// `provideCharts({ registerables })` only registers when its BaseChartDirective
// is constructed, so any chart rendered via our own ThemedChartDirective (or
// raw `new Chart(...)`) on a page that does NOT also use ng2-charts would
// otherwise throw "<scale> is not a registered scale". Chart.register is
// idempotent — the subsequent ng2-charts registration is a safe no-op.
const CHART_REGISTERABLES = [
  CategoryScale,
  Filler,
  LineController,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Legend,
  TimeScale,
  Tooltip,
];
Chart.register(...CHART_REGISTERABLES);

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      // The first navigation only hydrates the server-rendered page, so it has
      // nothing to animate, and a page opened in a background tab has no
      // rendering to transition: Chrome aborts that transition with an
      // InvalidStateError, which Angular reports in dev mode.
      withViewTransitions({ skipInitialTransition: true }),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
    ),
    provideHttpClient(withFetch()),
    provideClientHydration(
      withHttpTransferCacheOptions({
        includePostRequests: true,
        // Every PostgREST call carries the public anon key as a bearer token,
        // which Angular counts as an auth header and skips by default. Without
        // this the transfer state held no responses at all, so every SSR page
        // refetched its data on hydration, the first client pass rendered
        // empty, and the server DOM was thrown away and rebuilt. Consumer SSR
        // routes never send user credentials (the portal is client-rendered).
        includeRequestsWithAuthHeaders: true,
        // The cache keeps no response headers unless listed. Product and
        // content lists read their total from PostgREST's Content-Range, so a
        // cached response without it made the paginator count NaN.
        includeHeaders: ['content-range'],
      }),
      withEventReplay(),
      withIncrementalHydration(),
      // Without this, Angular marks every component whose template has i18n
      // blocks `ngSkipHydration` on the server. ConsumerLayoutComponent is one,
      // so the whole consumer tree below it was re-rendered on the client
      // instead of hydrated (dev stats: "hydrated 1 component and 3 nodes").
      withI18nSupport()
    ),
    provideCharts({ registerables: CHART_REGISTERABLES }),
    // Image optimization runs through the live SE Netlify site until the .de/.no
    // Netlify sites exist; source images are on the shared Supabase bucket either way.
    // Revert to COUNTRY_CONFIG[environment.country].host once those sites are deployed.
    provideNetlifyLoader(COUNTRY_CONFIG.SE.host),
    provideZonelessChangeDetection(),
    {
      provide: ErrorHandler,
      useValue: new FilteredSentryErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => inject(VendorRegistryService).load()),
    provideAppInitializer(() => {
      inject(AltuidCookieService);
    }),
    ...portalDomainProviders,
    // The domain may not import the poll helper (portal-domain-no-portal-outside-domain), so
    // the composition root binds it: see report-job-poller.ts.
    { provide: REPORT_JOB_POLLER, useValue: pollJob },
  ],
};
